// TestmapcategoryIndex.jsx - Fixed version
import { useState, useCallback, useEffect, useMemo, useRef, useContext } from 'react';
import { Box, CssBaseline, Typography, Card, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import MapView from './MapView.jsx';
import TopToolbar from './TopToolbar.js';
import FormsBottomNavbar from '../Post Module/FormsBottomNavbar.js';
import { getVendors, getVendorWorkImages, getVendorsByCategory } from './api.js';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './../../AuthContext/AuthContext.js';

// Define worker types as constant
const WORKER_TYPES = [
  "Painting", "Carpenter", "Flooring", "AC Technician", "Cleaning maid",
  "Gardener", "Sofa Cleaning", "Water Purifier", "Kitchen/Toilet Cleaning", "Plumbing", "Electrical"
];

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://landnest.net:7000/';

const VendorCardContent = ({ vendor, isGuest }) => {
  const [images, setImages] = useState([]);
  const [imgIndex, setImgIndex] = useState(0);
  const [loadingImgs, setLoadingImgs] = useState(true);

  useEffect(() => {
    if (!vendor?.vendor_id) return;
    setLoadingImgs(true);
    setImgIndex(0);

    getVendorWorkImages(vendor.vendor_id)
      .then((data) => {
        if (data?.success && data.images?.length > 0) {
          const urls = data.images.map((img) =>
            img.url.startsWith('http') ? img.url : `${BASE_URL}/${img.url}`
          );
          setImages(urls);
        } else {
          setImages(vendor.profile ? [`${BASE_URL}/${vendor.profile}`] : []);
        }
      })
      .catch(() => {
        setImages(vendor.profile ? [`${BASE_URL}/${vendor.profile}`] : []);
      })
      .finally(() => setLoadingImgs(false));
  }, [vendor?.vendor_id, vendor?.profile]);

  const prev = (e) => {
    e.stopPropagation();
    setImgIndex((i) => (i - 1 + images.length) % images.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setImgIndex((i) => (i + 1) % images.length);
  };

  return (
    <Box sx={{ display: 'flex', gap: '4%' }}>
      <Box sx={{ position: 'relative', width: '25%', maxWidth: 100, flexShrink: 0 }}>
        {loadingImgs ? (
          <Box sx={{
            width: '100%',
            aspectRatio: '1/1',
            borderRadius: '10%',
            bgcolor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <CircularProgress size={20} />
          </Box>
        ) : images.length > 0 ? (
          <>
            <img
              src={images[imgIndex]}
              alt={vendor.name}
              style={{
                width: '100%',
                aspectRatio: '1/1',
                borderRadius: '10%',
                objectFit: 'cover',
                display: 'block',
              }}
              onError={(e) => {
                e.target.src = `${BASE_URL}/${vendor.profile}`;
              }}
            />

            {images.length > 1 && (
              <>
                <IconButton
                  size="small"
                  onClick={prev}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: -8,
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255,255,255,0.85)',
                    p: 0.2,
                    width: 20,
                    height: 20,
                    '&:hover': { bgcolor: '#fff' },
                  }}
                >
                  <ChevronLeft sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={next}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: -8,
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255,255,255,0.85)',
                    p: 0.2,
                    width: 20,
                    height: 20,
                    '&:hover': { bgcolor: '#fff' },
                  }}
                >
                  <ChevronRight sx={{ fontSize: 14 }} />
                </IconButton>
              </>
            )}

            {images.length > 1 && (
              <Box sx={{
                position: 'absolute',
                bottom: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '3px',
              }}>
                {images.map((_, i) => (
                  <Box
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setImgIndex(i); }}
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      bgcolor: i === imgIndex ? '#1976d2' : '#ccc',
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{
            width: '100%',
            aspectRatio: '1/1',
            borderRadius: '10%',
            bgcolor: '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography variant="caption" color="text.secondary">No img</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {vendor.profession}
        </Typography>
        <Typography variant="body2">Name: {vendor.name}</Typography>
        <Typography variant="body2">Mobile: {vendor.mobile}</Typography>
        <Typography variant="body2">Email: {vendor.email}</Typography>
        <Typography variant="body2">Experience: {vendor.experience}+ year(s)</Typography>
        <Typography variant="body2">⭐⭐⭐⭐</Typography>
        {isGuest && (
          <Typography variant="caption" color="error">
            Login to view full details
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const TestmapcategoryIndex = () => {
  const navigate = useNavigate();
  const { userId, isGuest } = useContext(AuthContext);
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openGuestModal, setOpenGuestModal] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const mapRef = useRef(null);
  const [viewport, setViewport] = useState({
    bounds: { south: 6.5, north: 35.5, west: 68, east: 97.5 },
    zoom: 5,
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  // Track if initial load is done
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Fetch all vendors on initial load
  const fetchAllVendors = async () => {
    setLoading(true);
    try {
      const data = await getVendors();
      if (data.success && data.vendors) {
        setVendors(data.vendors);
        setFilteredVendors(data.vendors);
      } else if (Array.isArray(data)) {
        setVendors(data);
        setFilteredVendors(data);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  };

  // Fetch vendors by category from backend
  const fetchVendorsByCategory = async (category) => {
    if (!category) {
      fetchAllVendors();
      return;
    }
    
    setLoading(true);
    try {
      const data = await getVendorsByCategory(category);
      if (data.success && data.vendors) {
        setFilteredVendors(data.vendors);
      } else if (Array.isArray(data)) {
        setFilteredVendors(data);
      } else {
        setFilteredVendors([]);
      }
    } catch (error) {
      console.error('Error fetching vendors by category:', error);
      setFilteredVendors([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle category selection from toolbar
  const handleCategorySelect = useCallback(async (category) => {
    setSelectedCategory(category);
    setSelectedVendor(null); // Clear selected vendor when changing category
    await fetchVendorsByCategory(category);
  }, []);

  // Initial load
  useEffect(() => {
    fetchAllVendors();
  }, []);

  const handleMarkerClick = useCallback((vendor) => {
    setSelectedVendor(vendor);
  }, []);

  const handleCardClick = () => {
    if (isGuest) {
      setOpenGuestModal(true);
    } else {
      navigate(`/work-details/${selectedVendor.vendor_id}`);
    }
  };

  const handleLocationSelect = useCallback((loc) => {
    if (loc) {
      const lat = loc.lat ?? loc.latitude;
      const lng = loc.lng ?? loc.lon ?? loc.longitude;
      if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
        setFlyTarget({ lat: parseFloat(lat), lng: parseFloat(lng), zoom: 13, key: Date.now() });
      }
    }
  }, []);

  const handleClearLocation = useCallback(() => {
    setFlyTarget({ lat: 20.5937, lng: 78.9629, zoom: 5, key: Date.now() });
  }, []);

  const handlePropertiesUpdate = useCallback((props) => {
    console.log('Properties received:', props);
  }, []);

  const filters = useMemo(() => ({
    propertyType: [],
    priceMin: 0,
    priceMax: 10000000000,
  }), []);

  // Get vendor counts for categories
  const vendorCounts = useMemo(() => {
    return vendors.reduce((acc, v) => {
      acc[v.profession] = (acc[v.profession] || 0) + 1;
      return acc;
    }, {});
  }, [vendors]);

  return (
    <>
      <CssBaseline />
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', bgcolor: 'rgb(239, 231, 221)' }}>
        <MapView
          ref={mapRef}
          onViewportChange={setViewport}
          onPropertiesUpdate={handlePropertiesUpdate}
          filters={filters}
          selectedId={selectedVendor?.vendor_id}
          onMarkerClick={handleMarkerClick}
          flyTarget={flyTarget}
          vendors={filteredVendors} // Pass filtered vendors to map
        />

        <TopToolbar
          onLocationSelect={handleLocationSelect}
          onClearLocation={handleClearLocation}
          onCategorySelect={handleCategorySelect}
          workerTypes={WORKER_TYPES} // Pass static worker types
          selectedCategory={selectedCategory}
          vendorCounts={vendorCounts}
        />

        {/* Loader */}
        {loading && (
          <Box sx={{
            display: 'flex', justifyContent: 'center', mt: 4,
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)', zIndex: 1000
          }}>
            <CircularProgress />
          </Box>
        )}

        {/* No vendors message */}
        {!loading && initialLoadDone && filteredVendors.length === 0 && (
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            px: 2, textAlign: 'center', zIndex: 1000
          }}>
            <Box sx={{ backgroundColor: 'rgba(255,255,255,0.9)', padding: '16px', borderRadius: '8px' }}>
              <Typography variant="h6">
                {selectedCategory ? `No ${selectedCategory} workers available` : 'No workers available'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {selectedCategory 
                  ? `We couldn't find any ${selectedCategory} workers in your area`
                  : "We couldn't find any workers in your area"}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Vendor Card at Bottom */}
        {selectedVendor && (
          <Box sx={{
            position: 'absolute',
            bottom: '80px',
            left: 0,
            right: 0,
            px: '4%',
            zIndex: 999,
          }}>
            <Card
              onClick={handleCardClick}
              sx={{
                borderRadius: '1rem',
                p: '4%',
                backgroundColor: '#fff',
                boxShadow: 3,
                width: '93%',
                cursor: 'pointer',
              }}
            >
              <VendorCardContent vendor={selectedVendor} isGuest={isGuest} />
            </Card>
          </Box>
        )}

        {/* Guest Login Dialog */}
        <Dialog open={openGuestModal} onClose={() => setOpenGuestModal(false)}>
          <DialogTitle>Login Required</DialogTitle>
          <DialogContent>
            <Typography>Please Register and login to access this feature.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenGuestModal(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => navigate('/')}>Login</Button>
          </DialogActions>
        </Dialog>
      </Box>
      <FormsBottomNavbar />
    </>
  );
};

export default TestmapcategoryIndex;